import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField';
import ByzMuiSaveButton from 'src/components/core-components/mui/buttons/ByzMuiSaveButton';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import { useBaseMutationWithVariables } from 'src/api/core/useBaseQuery';

interface LiquidInDetailModalProps {
  parentId: number;
  fbId: number;
  startingAmount: number;
  remainingAmount: number;
  deliverySpeed: number;
  onClose: () => void;
  onSave?: () => void;
}

const LiquidInDetailModal: React.FC<LiquidInDetailModalProps> = ({ parentId, fbId, startingAmount, remainingAmount, deliverySpeed, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    amount: deliverySpeed > 0 ? Math.min(deliverySpeed, remainingAmount).toString() : '',
    speed: deliverySpeed > 0 ? deliverySpeed.toString() : '',
    ptime: new Date().toISOString().slice(0, 16), // datetime-local format
  });

  const { mutate: createFluidIntakeDetail, isPending } = useBaseMutationWithVariables({
    endpoint: `/reference/api/Card/CreateFluidBalanceFluidIntDet`,
    method: 'POST',
  });

  const sanitizePositiveNumber = (value: string): string => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    const sanitized = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : cleaned;
    const num = parseFloat(sanitized);
    if (isNaN(num) || num < 0) return '';
    return sanitized;
  };

  const handleChange = (field: string, value: any) => {
    if (field === 'ptime') {
      setFormData(prev => ({ ...prev, [field]: value }));
      return;
    }

    let sanitizedValue = sanitizePositiveNumber(value);
    
    if (field === 'amount' && sanitizedValue !== '') {
      const numValue = parseFloat(sanitizedValue);
      if (numValue > remainingAmount) {
        alert(`Maksimum ${remainingAmount} ml girebilirsiniz.`);
        sanitizedValue = remainingAmount.toString();
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
  };

  const toLocalISOString = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleSave = () => {
    const amount = Number(formData.amount);
    
    const finalAmount = Math.min(amount, remainingAmount);
    
    const payload = {
      FbId: fbId,
      FbiId: parentId,
      Amount: finalAmount,
      DeliverySpeed: Number(formData.speed),
      Ptime: toLocalISOString(new Date(formData.ptime)),
    };

    createFluidIntakeDetail(payload, {
      onSuccess: () => {
        if (onSave) {
          onSave();
        }
        onClose();
      },
      onError: (error) => {
        console.error('Detay kaydedilemedi:', error);
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Başlangıç Miktarı (ml)
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Başlangıç Miktarı (ml)"
              type="number"
              value={startingAmount}
              disabled
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Miktarı (ml)
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Miktarı (ml)"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Hız (ml/sa)
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Hız (ml/sa)"
              type="number"
              value={formData.speed}
              onChange={(e) => handleChange('speed', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              İşlem Zamanı
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="İşlem Zamanı"
              type="datetime-local"
              value={formData.ptime}
              onChange={(e) => handleChange('ptime', e.target.value)}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <ByzMuiSaveButton onClick={handleSave} disabled={isPending} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LiquidInDetailModal;
